import { DropdownMenu, Flex, IconButton } from '@radix-ui/themes';
import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CountryFlag, countryCodeForAppLanguage } from '../../utils/languageFlag';

const languageNames: Record<string, string> = {
  en: 'English',
  sv: 'Svenska',
  fr: 'Français',
  es: 'Español',
  de: 'Deutsch',
};

/** `onLight`: a small grey button instead of the navbar's white icon, e.g. for use inside a dialog. */
const LanguageGlobe = ({ asSubmenu = false, onLight = false }: { asSubmenu?: boolean; onLight?: boolean }) => {
  const { t, i18n } = useTranslation();

  const supportedLanguages = (i18n.options.supportedLngs || [])
    .filter((lng): lng is string => typeof lng === 'string' && lng !== 'cimode' && lng !== 'pseudo')
    .sort();

  const items = supportedLanguages.map((lang) => (
    <DropdownMenu.Item
      key={lang}
      className={`dropdown-link${lang === i18n.language ? ' selected-item' : ''}`}
      onClick={() => i18n.changeLanguage(lang)}
    >
      <Flex as="span" align="center" gap="1" display="inline-flex">
        <CountryFlag countryCode={countryCodeForAppLanguage(lang)} />
        {languageNames[lang] || lang.toUpperCase()}
      </Flex>
    </DropdownMenu.Item>
  ));

  if (asSubmenu) {
    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger className="dropdown-link flex items-center">
          <Globe size={18} className="mr-2" /> {t('nav.languageGlobe.label')}
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubContent>{items}</DropdownMenu.SubContent>
      </DropdownMenu.Sub>
    );
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger style={{ backgroundColor: 'transparent' }}>
        {onLight ? (
          <IconButton
            size="2"
            variant="soft"
            color="gray"
            aria-label={t('nav.languageGlobe.label')}
            className="cursor-pointer"
            // The trigger above makes its child transparent; the grey background is set here.
            style={{ backgroundColor: 'var(--gray-3)' }}
          >
            <Globe size={16} />
          </IconButton>
        ) : (
          <IconButton
            color="gray"
            highContrast
            aria-label={t('nav.languageGlobe.label')}
            className="navbar-item cursor-pointer mx-2"
          >
            <Globe size={20} />
          </IconButton>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content color="gray" variant="soft" align="end">
        {items}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default LanguageGlobe;
